"use client";

import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";
import { motion } from "motion/react";
import { Play, Pause, Volume2, Mic, Globe } from "lucide-react";
import { useState, useRef, useEffect } from "react";

const voices = [
  { name: "Mira", accent: "American", gender: "Female", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hi there, how are you doing today?" },
  { name: "Kai", accent: "American", gender: "Male", voiceId: "TxGEqnHWrfWFTfGW9XjX", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hey, what's going on everybody?" },
  { name: "Marcus", accent: "American", gender: "Male", voiceId: "nPczCjzI2devNBz1zQrb", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hello everyone, welcome back!" },
  { name: "Stella", accent: "American", gender: "Female", voiceId: "9BWtsMINqrJLrRacOk9x", flag: "\u{1F1FA}\u{1F1F8}", hello: "Welcome to the show, let's get started!" },
  { name: "Hudson", accent: "American", gender: "Male", voiceId: "cjVigY5qzO86Huf0OWal", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hey folks, great to see you all here!" },
  { name: "Elena", accent: "American", gender: "Female", voiceId: "FGY2WhTYpPnrIDTdsKH5", flag: "\u{1F1FA}\u{1F1F8}", hello: "Good morning! Ready for something amazing?" },
  { name: "Jaxon", accent: "American", gender: "Male", voiceId: "29vD33N1CtxCmqQRPOHJ", flag: "\u{1F1FA}\u{1F1F8}", hello: "What's up? Let me tell you a story." },
  { name: "Ava", accent: "American", gender: "Female", voiceId: "EXAVITQu4vr4xnSDxMaL", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hi! Nice to meet you!" },
  { name: "Cole", accent: "American", gender: "Male", voiceId: "iP95p4xoKVk53GoZ742B", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hey there! How's everyone doing?" },
  { name: "Ruby", accent: "American", gender: "Female", voiceId: "pFZP5JQG7iQjIQuC4Bku", flag: "\u{1F1FA}\u{1F1F8}", hello: "Hello! So happy to be here with you!" },
  { name: "Oliver", accent: "British", gender: "Male", voiceId: "onwK4e9ZLuTAKqWW03F9", flag: "\u{1F1EC}\u{1F1E7}", hello: "Good day to you, lovely weather!" },
  { name: "Emma", accent: "British", gender: "Female", voiceId: "Xb7hH8MSUJpSbSDYk0k2", flag: "\u{1F1EC}\u{1F1E7}", hello: "Lovely to meet you, how do you do?" },
  { name: "Defne", accent: "Turkish", gender: "Female", voiceId: "dKmdJ8jq2hPAyWUBHu3C", flag: "\u{1F1F9}\u{1F1F7}", hello: "Merhaba, nas\u0131ls\u0131n\u0131z?" },
  { name: "Sofia", accent: "Spanish", gender: "Female", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1EA}\u{1F1F8}", hello: "\u00A1Hola! \u00BFC\u00F3mo est\u00E1s?" },
  { name: "Pierre", accent: "French", gender: "Male", voiceId: "pNInz6obpgDQGcFmaJgB", flag: "\u{1F1EB}\u{1F1F7}", hello: "Bonjour \u00E0 tous!" },
  { name: "Hans", accent: "German", gender: "Male", voiceId: "pNInz6obpgDQGcFmaJgB", flag: "\u{1F1E9}\u{1F1EA}", hello: "Hallo, wie geht's?" },
  { name: "Fatima", accent: "Arabic", gender: "Female", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1F8}\u{1F1E6}", hello: "\u0645\u0631\u062D\u0628\u0627\u064B\u060C \u0643\u064A\u0641 \u062D\u0627\u0644\u0643\u061F" },
  { name: "Natasha", accent: "Russian", gender: "Female", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1F7}\u{1F1FA}", hello: "\u041F\u0440\u0438\u0432\u0435\u0442! \u041A\u0430\u043A \u0434\u0435\u043B\u0430?" },
  // Creaneo voices \u2014 voiceId "creaneo:<id>" maps to the proprietary engine.
  { name: "Creaneo Nova", accent: "Creaneo", gender: "Female", voiceId: "creaneo:eve", flag: "\u2728", hello: "Hey! I'm Nova, a Creaneo voice." },
  { name: "Creaneo Aura", accent: "Creaneo", gender: "Female", voiceId: "creaneo:ara", flag: "\u2728", hello: "Hi there, lovely to meet you!" },
  { name: "Creaneo Rex", accent: "Creaneo", gender: "Male", voiceId: "creaneo:rex", flag: "\u2728", hello: "Hello, let's create something great." },
  { name: "Creaneo Sol", accent: "Creaneo", gender: "Male", voiceId: "creaneo:sal", flag: "\u2728", hello: "Smooth and clear, that's how I roll." },
  { name: "Creaneo Atlas", accent: "Creaneo", gender: "Male", voiceId: "creaneo:leo", flag: "\u2728", hello: "Strong, confident, ready to go." },
];

const langStats = [
  { flag: "\u{1F1F9}\u{1F1F7}", count: "92+" },
  { flag: "\u{1F1FA}\u{1F1F8}", count: "139+" },
  { flag: "\u{1F1F8}\u{1F1E6}", count: "84+" },
  { flag: "\u{1F1EB}\u{1F1F7}", count: "72+" },
  { flag: "\u{1F1E9}\u{1F1EA}", count: "60+" },
  { flag: "\u{1F1EA}\u{1F1F8}", count: "55+" },
  { flag: "\u{1F1EF}\u{1F1F5}", count: "50+" },
  { flag: "\u{1F1E8}\u{1F1F3}", count: "49+" },
  { flag: "\u{1F1F0}\u{1F1F7}", count: "48+" },
  { flag: "\u{1F1EE}\u{1F1F3}", count: "46+" },
  { flag: "\u{1F1F7}\u{1F1FA}", count: "46+" },
  { flag: "\u{1F1EE}\u{1F1F9}", count: "44+" },
  { flag: "\u{1F1E7}\u{1F1F7}", count: "44+" },
];

export function Voices() {
  const { T } = useLang();
  const U = useUpper();
  const [playing, setPlaying] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [studioLoading, setStudioLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setMounted(true);
    setLoggedIn(!!localStorage.getItem("speakroar-user"));
  }, []);

  const openStudio = async () => {
    setStudioLoading(true);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://studio.speakroar.com";
    try {
      const resp = await fetch("/api/auth/create-otp", { method: "POST" });
      const data = await resp.json();
      if (data.success && data.token) {
        window.open(`${apiUrl}/auto-login/${data.token}`, "_blank");
      } else {
        window.open(apiUrl, "_blank");
      }
    } catch {
      window.open(apiUrl, "_blank");
    } finally {
      setStudioLoading(false);
    }
  };

  const playVoice = (voice: typeof voices[0]) => {
    if (playing === voice.name) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    setPlaying(null);
    setLoading(voice.name);
    fetch("/api/public/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ voice_id: voice.voiceId, accent: voice.accent, text: voice.hello }),
    })
      .then(r => { if (!r.ok) throw new Error("Failed"); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => setPlaying(null);
        audio.play()
          .then(() => { setPlaying(voice.name); setLoading(null); })
          .catch(() => { setPlaying(null); setLoading(null); URL.revokeObjectURL(url); });
      })
      .catch(() => { setLoading(null); setPlaying(null); });
  };

  return (
    <section id="sesler" className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30 mb-4"
          >
            {U(T("voices.badge"))}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white">
            {T("voices.title")}
          </h2>
          <p className="text-neutral-600 dark:text-slate-300 mt-4 text-base max-w-lg mx-auto">
            {T("voices.desc")}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="flex justify-center gap-3 mb-12 flex-wrap">
          {langStats.map((l, i) => (
            <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-50 dark:bg-slate-900/50 border border-neutral-200 dark:border-white/10 rounded-full text-xs font-semibold text-neutral-600 dark:text-slate-200">
              <span className="text-sm">{l.flag}</span> {l.count}
            </div>
          ))}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 dark:bg-cyan-500/15 border border-cyan-200 dark:border-cyan-400/30 rounded-full text-xs font-bold text-cyan-600 dark:text-cyan-300">
            <Globe size={12} /> 25+ {T("voices.languages")}
          </div>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          {voices.map((voice, i) => {
            const isPlaying = playing === voice.name;
            const isLoading = loading === voice.name;
            return (
              <motion.div
                key={voice.name}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.5, type: "spring", stiffness: 120 }}
                onClick={() => playVoice(voice)}
                className={`group relative flex flex-col items-center p-4 rounded-2xl cursor-pointer transition-all duration-300 ${
                  isPlaying
                    ? "bg-gradient-to-b from-cyan-50 to-teal-50 dark:from-cyan-500/15 dark:to-teal-500/10 ring-2 ring-cyan-400 shadow-xl shadow-cyan-500/15 scale-[1.05] z-10"
                    : "bg-neutral-50 dark:bg-slate-900/50 border border-neutral-200 dark:border-white/10 hover:bg-white dark:hover:bg-white/5 hover:border-cyan-200 dark:hover:border-cyan-400/40 hover:shadow-lg hover:shadow-cyan-500/8 hover:scale-[1.02]"
                }`}
              >
                <div className="absolute -top-2 -right-1 text-lg drop-shadow-sm">{voice.flag}</div>

                <div className="relative mb-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                    voice.gender === "Female"
                      ? "bg-gradient-to-br from-pink-400 to-rose-500 shadow-lg shadow-pink-500/25"
                      : "bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg shadow-blue-500/25"
                  }`}>
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : isPlaying ? (
                      <Pause size={18} className="text-white" />
                    ) : (
                      <span className="text-white font-bold text-base">{voice.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </div>

                  {!isPlaying && !isLoading && (
                    <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/15 flex items-center justify-center transition-all">
                      <Play size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 drop-shadow" />
                    </div>
                  )}

                  {isPlaying && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-end gap-[2px]">
                      {[0, 1, 2, 3, 4].map(j => (
                        <motion.div
                          key={j}
                          className="w-[3px] rounded-full bg-gradient-to-t from-cyan-500 to-teal-400"
                          animate={{ height: [3, 14, 5, 16, 3] }}
                          transition={{ duration: 0.7, repeat: Infinity, delay: j * 0.08 }}
                        />
                      ))}
                    </div>
                  )}

                  {isPlaying && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-cyan-400"
                      animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </div>

                <p className={`text-xs font-medium text-center leading-tight min-h-[2rem] flex items-center transition-colors ${
                  isPlaying ? "text-cyan-600 dark:text-cyan-300" : "text-neutral-400 dark:text-slate-500"
                }`}>
                  &ldquo;{voice.hello}&rdquo;
                </p>

                <h3 className="text-sm font-bold text-neutral-900 dark:text-white mt-1">{voice.name}</h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[10px] text-neutral-400 dark:text-slate-500">{voice.accent}</span>
                  <span className={`w-1 h-1 rounded-full ${voice.gender === "Female" ? "bg-pink-400" : "bg-blue-400"}`} />
                  <span className={`text-[10px] font-semibold ${voice.gender === "Female" ? "text-pink-400" : "text-blue-400"}`}>
                    {voice.gender === "Female" ? "\u2640" : "\u2642"}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-12">
          <div className="inline-flex items-center gap-3 bg-neutral-50 dark:bg-slate-900/50 border border-neutral-200 dark:border-white/10 rounded-full px-6 py-3">
            <Mic size={16} className="text-cyan-500 dark:text-cyan-400" />
            <span className="text-sm text-neutral-600 dark:text-slate-200 font-medium">
              {T("voices.more")}
            </span>
            {mounted && loggedIn ? (
              <button
                onClick={openStudio}
                disabled={studioLoading}
                className="text-sm font-bold text-cyan-600 dark:text-cyan-300 hover:text-cyan-700 transition-colors disabled:opacity-50"
              >
                {studioLoading ? "..." : `${T("voices.open_studio")} \u2192`}
              </button>
            ) : (
              <a href="/register" className="text-sm font-bold text-cyan-600 dark:text-cyan-300 hover:text-cyan-700 transition-colors">
                {T("voices.try_free")}
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
